import { playerAnalyticsModel as player } from '$/player'
import type { IEpgCategoryInfoOVP, IEpgInfoOVP } from '@setplex/tria-api'
import { attach, sample } from 'effector'
import { and } from 'patronum'
import type { SendEventParams } from '~/shared/analytix'
import * as epgAnalyticsWidgetModel from '~/widgets/epg/analytics'
import { LiveModuleViewEventPayload } from '~/widgets/epg/index.h'
import { logErrorFx } from '../lib'
import { $readyAllInfo } from '../session/model'
import { $analytix, $analytixEnabled } from '../wrapper'

const $epgAnalytixCanSend = and($readyAllInfo, $analytixEnabled)

const toEpgView = (d: IEpgInfoOVP): SendEventParams.EpgView => ({
  channelId: String(d.channelId ?? ''),
  channelTitle: String(d.channelTitle ?? ''),
  tabId: String(d.tabId ?? ''),
})

const toEpgCategory = (
  d: IEpgCategoryInfoOVP
): SendEventParams.EpgCategory => ({
  categoryId: String(d.categoryId ?? ''),
  categoryTitle: String(d.categoryTitle ?? ''),
})

type EpgChannelSource = {
  channelId: string
  channelTitle: string
  channelOrderPosition: string
  categoryId?: string
  categoryTitle?: string
}

const toEpgChannel = (d: EpgChannelSource): SendEventParams.EpgChannel => ({
  channelId: String(d.channelId ?? ''),
  channelTitle: String(d.channelTitle ?? ''),
  categoryId: String(d.categoryId ?? ''),
  categoryTitle: String(d.categoryTitle ?? ''),
  channelOrder: String(d.channelOrderPosition ?? ''),
})

type EpgProgramWidgetPayload = {
  channelId: string
  channelTitle: string
  channelOrderPosition: string
  programTitle: string
  programStart: string
  programEnd: string
  programStatus: string
}

const toLiveModuleView = (
  d: LiveModuleViewEventPayload
): SendEventParams.LiveModuleView => ({
  previousView: d.prevView,
  currentView: d.currentView,
  regionTitle: d.regionTitle,
})

const toEpgProgram = (
  d: EpgProgramWidgetPayload & { categoryId?: string; categoryTitle?: string }
): SendEventParams.EpgProgram => ({
  channelId: String(d.channelId ?? ''),
  channelTitle: String(d.channelTitle ?? ''),
  categoryId: String(d.categoryId ?? ''),
  categoryTitle: String(d.categoryTitle ?? ''),
  channelOrder: String(d.channelOrderPosition ?? ''),
  programTitle: String(d.programTitle ?? ''),
  programStart: String(d.programStart ?? ''),
  programStatus: String(d.programStatus ?? ''),
  programEnd: String(d.programEnd ?? ''),
})

export const sendEpgViewFx = attach({
  source: $analytix,
  effect: (analytix, params: SendEventParams.EpgView) =>
    analytix.sendEpgViewEvent(params),
})

export const sendEpgCategoryFx = attach({
  source: $analytix,
  effect: (analytix, params: SendEventParams.EpgCategory) =>
    analytix.sendEpgCategoryEvent(params),
})

export const sendEpgChannelFx = attach({
  source: $analytix,
  effect: (analytix, params: SendEventParams.EpgChannel) =>
    analytix.sendEpgChannelEvent(params),
})

export const sendEpgProgramFx = attach({
  source: $analytix,
  effect: (analytix, params: SendEventParams.EpgProgram) =>
    analytix.sendEpgProgramEvent(params),
})

export const sendLiveModuleViewFx = attach({
  source: $analytix,
  effect: (analytix, params: SendEventParams.LiveModuleView) =>
    analytix.sendLiveModuleViewEvent(params),
})

for (const fx of [
  sendEpgViewFx,
  sendEpgCategoryFx,
  sendEpgChannelFx,
  sendEpgProgramFx,
  sendLiveModuleViewFx,
]) {
  sample({
    clock: fx.failData,
    fn: (e) => e,
    target: logErrorFx,
  })
}

// epg_view — same behavior as legacy `processes/analytics/model`
sample({
  clock: player.sendEpgViewEventData,
  source: {
    epgViewEventData: player.$epgViewEventData,
    canSend: $epgAnalytixCanSend,
  },
  filter: ({ epgViewEventData, canSend }) =>
    Boolean(epgViewEventData) && Boolean(canSend),
  fn: ({ epgViewEventData }, tabId) => {
    if (!epgViewEventData) {
      return toEpgView({})
    }
    if (tabId) {
      return toEpgView({
        ...epgViewEventData,
        tabId: String(tabId),
      })
    }
    return toEpgView(epgViewEventData)
  },
  target: sendEpgViewFx,
})

sample({
  clock: epgAnalyticsWidgetModel.sendEpgCategoryEventData,
  source: { canSend: $epgAnalytixCanSend },
  filter: ({ canSend }) => Boolean(canSend),
  fn: (_, d) => toEpgCategory(d),
  target: sendEpgCategoryFx,
})

sample({
  clock: epgAnalyticsWidgetModel.sendEpgChannelEventData,
  source: {
    canSend: $epgAnalytixCanSend,
    category: epgAnalyticsWidgetModel.$activeEpgCategory,
  },
  filter: ({ canSend }) => Boolean(canSend),
  fn: ({ category }, epgInfo) =>
    toEpgChannel({
      ...epgInfo,
      categoryId: category?.categoryId,
      categoryTitle: category?.categoryTitle,
    }),
  target: sendEpgChannelFx,
})

sample({
  clock: epgAnalyticsWidgetModel.sendEpgProgramEventData,
  source: {
    canSend: $epgAnalytixCanSend,
    category: epgAnalyticsWidgetModel.$activeEpgCategory,
  },
  filter: ({ canSend }) => Boolean(canSend),
  fn: ({ category }, epgInfo) =>
    toEpgProgram({
      ...epgInfo,
      categoryId: category?.categoryId,
      categoryTitle: category?.categoryTitle,
    }),
  target: sendEpgProgramFx,
})

sample({
  clock: epgAnalyticsWidgetModel.sendLiveModuleViewEvent,
  source: { canSend: $epgAnalytixCanSend },
  filter: ({ canSend }) => Boolean(canSend),
  fn: (_, d) => toLiveModuleView(d),
  target: sendLiveModuleViewFx,
})
